import React from 'react';

import { Dropdown, MenuProps, Popconfirm, Space, Tooltip } from 'antd-v5';
import type { ColumnsType } from 'antd-v5/es/table';
import {
  MdAssignment,
  MdEdit,
  MdExpandMore,
  MdOutlineFilterList,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';
import { cssVars } from 'scouting-ui/lib/variables';

import type { CounselorMeritBadge, MBCRecord } from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { PersonAvatar } from '@shared/components';
import { getPreferredName } from '@shared/utils/preferredName';
import sorters from '@utils/sorters';

import {
  AvailabilityFilterOptions,
  CounselorAvailabilityShort,
  MBCSearchTableFields,
  NON_US_VALUE,
  SearchTableActions,
  TableMode,
} from '../constants';
import { numberWithDecimal } from '../duck/utils';
import styles from './MBCSearchPage.less';
import { MBListPopover } from './MBListPopover';
import PersonNameWithContact from './PersonNameWithContact/PersonNameWithContact';

enum ColWidth {
  XSMALL = 60,
  SMALL = 80,
  MEDIUM = 90,
  LARGE = 120,
  XLARGE = 140,
}

/**
 * Build fruit filters from dataSource
 */
const buildMeritBadgeFilters = (data: MBCRecord[]) =>
  Array.from(
    new Set(
      data.flatMap((mb: MBCRecord) =>
        mb.meritBadges.map((mb: CounselorMeritBadge) => mb.meritBadgeName),
      ),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map(mb => ({
      text: mb,
      value: mb,
    }));

const buildNameFilters = (data: MBCRecord[]) =>
  Array.from(data).map(record => {
    const personFullName = getPreferredName(record);

    return {
      text: personFullName,
      value: personFullName,
    };
  });

const buildMemberIdFilters = (data: MBCRecord[]) =>
  Array.from(data).map(record => ({
    text: record.memberId.toString(),
    value: record.memberId,
  }));

export const MbcSearchTableColumn = (
  dataSource: MBCRecord[],
  showDistance: boolean,
  isCouncilOrg: boolean,
  hasEditAssignPerm: boolean,
  onEdit?: (memberId: number) => () => void,
  onAction?: (key: SearchTableActions, counselor: MBCRecord) => void,
  mode?: TableMode,
  searchType?: string,
  editMBsWarning?: string,
): ColumnsType<MBCRecord> => {
  const enableSortWithAPI = featureFlags.getFlag('SBL_6789_SORT_WITH_API');
  const searchWithZip = showDistance;
  const meritBadgeFilters = buildMeritBadgeFilters(dataSource);
  const nameFilters = buildNameFilters(dataSource);
  const memberIdFilters = buildMemberIdFilters(dataSource);

  return [
    {
      dataIndex: 'profilePicture',
      key: 'profilePicture',
      width: '3.8rem',
      render: (profilePicture: string, row: MBCRecord) => (
        <PersonAvatar
          isAdult={true}
          gender={row.gender}
          pictureUrl={profilePicture}
        />
      ),
    },
    {
      title: <FormattedMessage id="page.mbcSearch.table.column.title.name" />,
      dataIndex: 'Name',
      key: 'lastName',
      width: '10em',
      minWidth: ColWidth.XLARGE,
      filters: nameFilters,
      filterSearch: true,
      onFilter: (value, record: MBCRecord) => {
        const search = (value as string).toLowerCase();
        const personFullName = getPreferredName(record);

        return personFullName.toLowerCase().includes(search);
      },
      filterIcon: (filtered: boolean) => (
        <MdOutlineFilterList
          size={16}
          color={filtered ? cssVars['color-info'] : cssVars['color-white']}
        />
      ),
      render: (_value: unknown, row: MBCRecord) => (
        <PersonNameWithContact record={row} />
      ),
      ...(enableSortWithAPI
        ? {
            sorter: true,
          }
        : {
            sorter: (personA: MBCRecord, personB: MBCRecord) => {
              const personFullNameA = getPreferredName(personA);
              const personFullNameB = getPreferredName(personB);

              return personFullNameA.localeCompare(personFullNameB);
            },
          }),
    },
    ...(featureFlags.getFlag('SBL_6772_SHOW_MEMBER_ID_COLUMN')
      ? [
          {
            title: (
              <FormattedMessage id="page.mbcSearch.table.column.title.id" />
            ),
            dataIndex: 'memberId',
            key: 'memberId',
            width: ColWidth.MEDIUM,
            minWidth: ColWidth.MEDIUM,
            filters: memberIdFilters,
            onFilter: (value: boolean | number | string, record: MBCRecord) =>
              record.memberId?.toString().includes(value as string),
            filterSearch: true,
            filterIcon: (filtered: boolean) => (
              <MdOutlineFilterList
                size={16}
                color={
                  filtered ? cssVars['color-info'] : cssVars['color-white']
                }
              />
            ),
          },
        ]
      : []),
    searchType === NON_US_VALUE
      ? {
          title: (
            <FormattedMessage id="page.mbcSearch.table.column.title.country" />
          ),
          align: 'center',
          dataIndex: 'countryName',
          width: ColWidth.LARGE,
          minWidth: ColWidth.LARGE,
          key: 'countryName',
        }
      : {
          title: (
            <FormattedMessage id="page.mbcSearch.table.column.title.city" />
          ),
          dataIndex: 'city',
          width: ColWidth.LARGE,
          minWidth: ColWidth.LARGE,
          key: 'city',
          render: (_value: unknown, mbc: MBCRecord) => {
            const citySate = [mbc?.city, mbc?.state].filter(Boolean).join(', ');
            return <Text className={styles.wrapNoBreak}>{citySate}</Text>;
          },
        },
    {
      title: (
        <FormattedMessage id="page.mbcSearch.table.column.title.zipCode" />
      ),
      align: 'center',
      dataIndex: 'zipCode',
      key: 'zipCode',
      width: ColWidth.SMALL,
      minWidth: ColWidth.SMALL,
      hidden: !searchWithZip,
    },
    {
      title: (
        <FormattedMessage id="page.mbcSearch.table.column.title.distance" />
      ),
      dataIndex: MBCSearchTableFields.DISTANCE,
      align: 'center',
      hidden: !searchWithZip,
      width: ColWidth.MEDIUM,
      key: MBCSearchTableFields.DISTANCE,
      render: (distance: number | null) => {
        const distanceValue = numberWithDecimal(distance, 2);
        return (
          <Text className={styles.wrapNoBreak}>
            <FormattedMessage
              id="page.mbcSearch.table.column.title.distance.value"
              values={{
                distance: distanceValue,
              }}
            />
          </Text>
        );
      },
      ...(enableSortWithAPI
        ? {
            sorter: true,
          }
        : {
            sorter: sorters.number(MBCSearchTableFields.DISTANCE),
          }),
    },
    {
      title: (
        <FormattedMessage id="page.mbcSearch.table.column.title.counseling" />
      ),
      dataIndex: 'scoutCount',
      width: '133px',
      minWidth: ColWidth.XLARGE,
      key: 'scoutCount',
      align: 'center',
    },
    {
      title: (
        <FormattedMessage id="page.mbcSearch.table.column.title.availability" />
      ),
      dataIndex: 'counselorAvailabilityShort',
      key: 'counselorAvailabilityShort',
      width: '13ch',
      render: (availability: string) => {
        const isAvailable =
          CounselorAvailabilityShort.AVAILABLE === availability;
        if (isAvailable) {
          return <Text>{AvailabilityFilterOptions.AVAILABLE}</Text>;
        }
        return <Text>{AvailabilityFilterOptions.BUSY}</Text>;
      },
    },
    {
      title: (
        <FormattedMessage id="page.mbcSearch.table.column.title.meritBadges" />
      ),
      dataIndex: 'meritBadges',
      width: 200,
      minWidth: ColWidth.XLARGE,
      key: 'meritBadges',
      filters: meritBadgeFilters,
      filterSearch: true,
      onFilter: (value, record: MBCRecord) =>
        record.meritBadges.some(
          mb => mb.meritBadgeName.toLowerCase() === String(value).toLowerCase(),
        ),
      filterIcon: (filtered: boolean) => (
        <MdOutlineFilterList
          size={16}
          color={filtered ? cssVars['color-info'] : cssVars['color-white']}
        />
      ),
      render: (meritBadges: CounselorMeritBadge[]) => (
        <MBListPopover meritBadges={meritBadges} />
      ),
    },
    {
      title: (
        <FormattedMessage id="page.mbcSearch.table.column.title.meritBadges.count" />
      ),
      dataIndex: 'meritBadges',
      key: 'count',
      width: '5em',
      minWidth: ColWidth.XSMALL,
      align: 'center',
      render: (meritBadges: CounselorMeritBadge[]) => {
        const mbCount = meritBadges?.length || 0;
        return <Text className={styles.wrapNoBreak}>{mbCount}</Text>;
      },
    },
    ...(!editMBsWarning
      ? [
          mode === TableMode.ASSIGN_MBCS
            ? {
                dataIndex: 'meritBadges',
                key: 'assignMbc',
                width: '5em',
                render: (_value: unknown, mbc: MBCRecord) => {
                  const handleClick = () => {
                    onAction?.(SearchTableActions.ASSIGN, mbc);
                  };
                  return (
                    <Space align="center">
                      <Tooltip title="Remove">
                        <Popconfirm
                          title={
                            <FormattedMessage id="mbcDashboard.mbTable.assignMbcConfirm.title" />
                          }
                          description={
                            <FormattedMessage
                              values={{
                                mbcName: `${mbc.firstName} ${mbc.lastName}`,
                              }}
                              id="mbcDashboard.mbTable.assignMbcConfirm"
                            />
                          }
                          onConfirm={handleClick}
                          okButtonProps={{ danger: true }}
                          cancelButtonProps={{
                            type: 'default',
                          }}
                          onCancel={() => null}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button shape="round" type="text" size="small">
                            <FormattedMessage id="youthProfile.AdvMbc.modal.assign" />
                          </Button>
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  );
                },
              }
            : {
                dataIndex: 'meritBadges',
                key: 'edit',
                width: '5em',
                render: (_value: unknown, mbc: MBCRecord) => {
                  const handleMenuClick: MenuProps['onClick'] = e => {
                    onAction?.(e.key as SearchTableActions, mbc);
                  };

                  const items = [
                    {
                      label: 'Assign',
                      key: 'assign',
                      icon: <MdAssignment />,
                    },
                  ];

                  if (hasEditAssignPerm) {
                    return (
                      <Space>
                        <Dropdown.Button
                          size="small"
                          type="default"
                          icon={<MdExpandMore />}
                          menu={{ items, onClick: handleMenuClick }}
                          onClick={onEdit?.(mbc.memberId)}
                        >
                          <Space>
                            <MdEdit className={styles.editIcon} />
                            <FormattedMessage id="shared.edit" />
                          </Space>
                        </Dropdown.Button>
                      </Space>
                    );
                  }

                  if (isCouncilOrg) {
                    return (
                      <Button
                        size="small"
                        type="default"
                        onClick={onEdit?.(mbc.memberId)}
                      >
                        <Space>
                          <MdEdit className={styles.editIcon} />
                          <FormattedMessage id="shared.edit" />
                        </Space>
                      </Button>
                    );
                  }

                  return (
                    <Space>
                      <Dropdown menu={{ items, onClick: handleMenuClick }}>
                        <Button
                          size="small"
                          type="default"
                          icon={<MdExpandMore />}
                          iconPosition="end"
                        >
                          <FormattedMessage id="shared.actions" />
                        </Button>
                      </Dropdown>
                    </Space>
                  );
                },
              },
        ]
      : []),
  ];
};
